import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Footer from "../Footer";

const API_BASE = "https://tn.server.goatteachers.com/api"; // adapt if needed

// --- Helpers ---
const stripPlus88 = (v = "") => v.replace(/^\+?88/, "");
const isValidBD11Digit = (v = "") => {
  const core = stripPlus88(String(v).trim());
  const digitsOnly = core.replace(/\D/g, "");
  return /^01[0-9]{9}$/.test(digitsOnly) && digitsOnly.length === 11;
};

const Chip = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full border transition-all text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400/40
      ${
        active
          ? "bg-indigo-600 text-white border-indigo-600 shadow"
          : "bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30"
      }
    `}
  >
    {children}
  </button>
);

const FieldLabel = ({ children }) => (
  <label className="block text-sm font-semibold text-gray-800 mb-1.5">
    {children}
  </label>
);

const EMPTY_FORM = {
  name: "",
  medium_id: "",
  level_id: "",
  subject_ids: [],
  division_id: "",
  district_id: "",
  area_ids: [],
  gender: "",
  poster_type: "Student",
  min_salary: "", // slider value (number or "") – 0/"" means optional
  max_salary: "",
  institute_preference_id: "",
  weekday_ids: [],
  phone: "",
  whatsapp: "",
  whatsapp_same_as_phone: false,
};

export default function TuitionPostForm() {
  const [error, setError] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [options, setOptions] = useState({
    divisions: [],
    districts: [],
    areas: [],
    mediums: [],
    levels: [],
    subjects: [],
    weekdays: [],
    institutePreferences: [],
  });

  const [form, setForm] = useState(EMPTY_FORM);

  const topRef = useRef(null);

  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  //  React-Select: remove "double border" by styling the control itself
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      borderRadius: 12,
      borderColor: state.isFocused
        ? "rgba(99,102,241,0.55)"
        : "rgba(209,213,219,1)", // gray-300
      boxShadow: state.isFocused ? "0 0 0 3px rgba(99,102,241,0.18)" : "none",
      "&:hover": {
        borderColor: state.isFocused
          ? "rgba(99,102,241,0.7)"
          : "rgba(156,163,175,1)", // gray-400
      },
      backgroundColor: "white",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 10px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "rgba(107,114,128,1)", // gray-500
    }),
    menu: (base) => ({
      ...base,
      zIndex: 50,
      borderRadius: 12,
      overflow: "hidden",
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: 9999,
      backgroundColor: "rgba(99,102,241,0.10)",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "rgba(67,56,202,1)",
      fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: 9999,
      ":hover": { backgroundColor: "rgba(99,102,241,0.18)" },
    }),
  };

  // Load dropdown options
  useEffect(() => {
    let isMounted = true;

    async function fetchOptions() {
      setLoadingOptions(true);
      setError("");

      try {
        const urls = [
          ["divisions", `${API_BASE}/divisions`],
          ["districts", `${API_BASE}/districts`],
          ["areas", `${API_BASE}/areas`],
          ["mediums", `${API_BASE}/mediums`],
          ["levels", `${API_BASE}/levels`],
          ["subjects", `${API_BASE}/subjects`],
          ["weekdays", `${API_BASE}/students/weekdays`],
          [
            "institutePreferences",
            `${API_BASE}/students/institute-preferences`,
          ],
        ];

        const responses = await Promise.all(
          urls.map(([k, u]) =>
            fetch(u).then((r) => {
              if (!r.ok) throw new Error(`${k} → ${r.status}`);
              return r.json();
            }),
          ),
        );

        if (!isMounted) return;

        setOptions({
          divisions: responses[0],
          districts: responses[1],
          areas: responses[2],
          mediums: responses[3],
          levels: responses[4],
          subjects: responses[5],
          weekdays: responses[6],
          institutePreferences: responses[7],
        });
      } catch (e) {
        if (isMounted) {
          setError("ড্রপডাউন লোড করা সম্ভব হয়নি। অনুগ্রহ করে রিফ্রেশ করুন।");
        }
        console.error(e);
      } finally {
        if (isMounted) setLoadingOptions(false);
      }
    }

    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  const districtsForDivision = useMemo(() => {
    if (!form.division_id) return options.districts;
    return options.districts.filter(
      (d) => String(d.division_id) === String(form.division_id),
    );
  }, [form.division_id, options.districts]);

  const areasForDistrict = useMemo(() => {
    if (!form.district_id) return options.areas;
    return options.areas.filter(
      (a) => String(a.district_id) === String(form.district_id),
    );
  }, [form.district_id, options.areas]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Required field validation
    if (
      !form.name ||
      !form.medium_id ||
      !form.level_id ||
      !form.subject_ids.length ||
      !form.division_id ||
      !form.district_id ||
      !form.area_ids.length ||
      !form.phone
    ) {
      setError("অনুগ্রহ করে সমস্ত আবশ্যক ক্ষেত্র পূর্ণ করুন।");
      scrollToTop();
      return;
    }

    // Phone validation
    if (!isValidBD11Digit(form.phone)) {
      setError("সঠিক ১১ ডিজিটের ফোন নম্বর দিন (01XXXXXXXXX)।");
      scrollToTop();
      return;
    }

    // WhatsApp: same-as-phone or custom
    const whatsappNumber = form.whatsapp_same_as_phone
      ? form.phone
      : form.whatsapp;

    if (whatsappNumber && !isValidBD11Digit(whatsappNumber)) {
      setError("সঠিক ১১ ডিজিটের হোয়াটসঅ্যাপ নম্বর দিন (01XXXXXXXXX)।");
      scrollToTop();
      return;
    }

    // Salary from slider (0 or empty = optional)
    const salaryValue =
      form.min_salary && Number(form.min_salary) > 0
        ? Number(form.min_salary)
        : null;

    setSubmitting(true);

    try {
      const payload = {
        student_name: form.name.trim(),
        poster_type: form.poster_type || "Student",
        medium_id: form.medium_id || null,
        level_id: form.level_id || null,
        gender_preference: form.gender || "Any",
        phone_number: stripPlus88(form.phone),
        whatsapp_number: whatsappNumber ? stripPlus88(whatsappNumber) : null,
        division_id: form.division_id || null,
        district_id: form.district_id || null,
        area_ids: form.area_ids,
        subject_ids: form.subject_ids,
        min_salary: salaryValue,
        max_salary: salaryValue,
        institute_preference_id: form.institute_preference_id || null,
        weekday_ids: form.weekday_ids,
      };

      const res = await fetch(`${API_BASE}/students/tuition-posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.message || "টিউশন পোস্ট করা যায়নি। একটু পরে আবার চেষ্টা করুন।",
        );
      }

      setSuccess("টিউশন পোস্ট সফলভাবে তৈরি হয়েছে!");
      scrollToTop();
      setForm(EMPTY_FORM);
    } catch (err) {
      console.error("Create tuition post error (frontend):", err);
      setError(
        err.message ||
          "সার্ভার সমস্যার কারণে টিউশন পোস্ট করা সম্ভব হয়নি। একটু পরে চেষ্টা করুন।",
      );
      scrollToTop();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="baloo-da-2-tutor
 min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50 py-10 px-4"
      >
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
            টিউশন পোস্ট করুন
          </h1>
          <p className="text-gray-600 mt-1">
            নীচে ফর্মটি পূর্ণ করে একটি টিউশন পোস্ট করুন।
          </p>
        </div>

        <div className="mx-auto max-w-2xl mt-6" ref={topRef}>
          {/* Tiny XP toast */}
          <div
            id="xp-toast"
            className="pointer-events-none fixed left-1/2 -translate-x-1/2 top-4 transform transition-all duration-300 opacity-0 translate-y-2"
          >
            <div className="rounded-full bg-emerald-600 text-white text-sm px-4 py-2 shadow-lg">
              +10 XP
            </div>
          </div>

          <div className="rounded-3xl bg-white/80 backdrop-blur shadow-xl border border-gray-100 p-6 md:p-8">
            {(error || success) && (
              <div className="space-y-3 mb-5">
                {error && (
                  <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
                    {success}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Poster type */}
              <div>
                <FieldLabel>কে টিউশন পোস্ট করছেন?</FieldLabel>
                <div className="flex flex-wrap gap-3">
                  <Chip
                    active={form.poster_type === "Student"}
                    onClick={() =>
                      setForm((p) => ({ ...p, poster_type: "Student" }))
                    }
                  >
                    শিক্ষার্থী
                  </Chip>
                  <Chip
                    active={form.poster_type === "Guardian"}
                    onClick={() =>
                      setForm((p) => ({ ...p, poster_type: "Guardian" }))
                    }
                  >
                    অভিভাবক
                  </Chip>
                  {/* <Chip
                    active={form.poster_type === "Tuition Media"}
                    onClick={() =>
                      setForm((p) => ({ ...p, poster_type: "Tuition Media" }))
                    }
                  >
                    টিউশন মিডিয়া
                  </Chip> */}
                </div>
              </div>

              {/* Name */}
              <div>
                <FieldLabel>শিক্ষার্থীর নাম (আবশ্যিক)</FieldLabel>
                <input
                  type="text"
                  className="text-black w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus:ring-4 focus:ring-indigo-400/25 focus:border-indigo-400 transition"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  required
                  placeholder="নাম"
                />
              </div>

              {/* Medium */}
              <div>
                <FieldLabel>মিডিয়াম</FieldLabel>
                <Select
                  name="medium"
                  options={options.mediums.map((m) => ({
                    value: m.id,
                    label: m.name,
                  }))}
                  value={
                    form.medium_id
                      ? {
                          value: form.medium_id,
                          label: options.mediums.find(
                            (m) => m.id === form.medium_id,
                          )?.name,
                        }
                      : null
                  }
                  onChange={(opt) =>
                    setForm((p) => ({ ...p, medium_id: opt ? opt.value : "" }))
                  }
                  isDisabled={loadingOptions}
                  isClearable
                  placeholder="আপনি কোন মিডিয়ামে পড়েন?"
                  styles={selectStyles}
                />
              </div>

              {/* Level */}
              <div>
                <FieldLabel>লেভেল</FieldLabel>
                <Select
                  name="level"
                  options={options.levels.map((l) => ({
                    value: l.id,
                    label: l.name,
                  }))}
                  value={
                    form.level_id
                      ? {
                          value: form.level_id,
                          label: options.levels.find(
                            (l) => l.id === form.level_id,
                          )?.name,
                        }
                      : null
                  }
                  onChange={(opt) =>
                    setForm((p) => ({ ...p, level_id: opt ? opt.value : "" }))
                  }
                  isDisabled={loadingOptions}
                  isClearable
                  placeholder="আপনি কোন লেভেলে পড়েন?"
                  styles={selectStyles}
                />
              </div>

              {/* Subjects */}
              <div>
                <FieldLabel>সাবজেক্ট</FieldLabel>
                <Select
                  isMulti
                  name="subjects"
                  options={options.subjects.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  value={options.subjects
                    .filter((s) => form.subject_ids.includes(s.id))
                    .map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(opts) =>
                    setForm((p) => ({
                      ...p,
                      subject_ids: (opts || []).map((o) => o.value),
                    }))
                  }
                  placeholder="আপনি কোন বিষয়ে পড়তে চান?"
                  isClearable
                  styles={selectStyles}
                />
              </div>

              {/* Institute preference */}
              <div>
                <FieldLabel>কোন ব্যাকগ্রউন্ডের টিউটর চাচ্ছেন</FieldLabel>
                <Select
                  name="institutePreference"
                  options={options.institutePreferences.map((i) => ({
                    value: i.id,
                    label: i.name,
                  }))}
                  value={
                    form.institute_preference_id
                      ? {
                          value: form.institute_preference_id,
                          label:
                            options.institutePreferences.find(
                              (i) => i.id === form.institute_preference_id,
                            )?.name || "",
                        }
                      : null
                  }
                  onChange={(opt) =>
                    setForm((p) => ({
                      ...p,
                      institute_preference_id: opt ? opt.value : "",
                    }))
                  }
                  isClearable
                  isDisabled={loadingOptions}
                  placeholder="যেকোনো / BUET / DU / মেডিকেল ইত্যাদি"
                  styles={selectStyles}
                />
              </div>

              {/* Location */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>বিভাগ</FieldLabel>
                  <Select
                    name="division"
                    options={options.divisions.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                    value={
                      form.division_id
                        ? {
                            value: form.division_id,
                            label: options.divisions.find(
                              (d) => d.id === form.division_id,
                            )?.name,
                          }
                        : null
                    }
                    onChange={(opt) => {
                      const selectedId = opt ? opt.value : "";
                      setForm((p) => ({
                        ...p,
                        division_id: selectedId,
                        district_id: "",
                        area_ids: [],
                      }));
                    }}
                    isDisabled={loadingOptions}
                    isClearable
                    placeholder="বিভাগ খুঁজুন..."
                    styles={selectStyles}
                  />
                </div>

                <div>
                  <FieldLabel>জেলা</FieldLabel>
                  <Select
                    name="district"
                    options={districtsForDivision.map((d) => ({
                      value: d.id,
                      label: d.name,
                    }))}
                    value={
                      form.district_id
                        ? {
                            value: form.district_id,
                            label: districtsForDivision.find(
                              (d) => d.id === form.district_id,
                            )?.name,
                          }
                        : null
                    }
                    onChange={(opt) => {
                      const selectedId = opt ? opt.value : "";
                      setForm((p) => ({
                        ...p,
                        district_id: selectedId,
                        area_ids: [],
                      }));
                    }}
                    isDisabled={loadingOptions || !form.division_id}
                    isClearable
                    placeholder="জেলা খুঁজুন..."
                    styles={selectStyles}
                  />
                </div>
              </div>

              <div>
                <FieldLabel>এলাকা(গুলি)</FieldLabel>
                <Select
                  isMulti
                  name="areas"
                  options={areasForDistrict.map((a) => ({
                    value: a.id,
                    label: a.name,
                  }))}
                  value={form.area_ids
                    .map((id) => areasForDistrict.find((a) => a.id === id))
                    .filter(Boolean)
                    .map((a) => ({ value: a.id, label: a.name }))}
                  onChange={(opts) =>
                    setForm((p) => ({
                      ...p,
                      area_ids: (opts || []).map((o) => o.value),
                    }))
                  }
                  isDisabled={loadingOptions || !form.district_id}
                  placeholder="এলাকা খুঁজুন..."
                  styles={selectStyles}
                />
              </div>

              {/* Weekdays */}
              <div>
                <FieldLabel>সপ্তাহে কয়দিন / কোন দিনগুলো টিউশন চান?</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {options.weekdays.map((day) => {
                    const active = (form.weekday_ids || []).includes(day.id);
                    return (
                      <Chip
                        key={day.id}
                        active={active}
                        onClick={() => {
                          setForm((p) => {
                            const current = p.weekday_ids || [];
                            const already = current.includes(day.id);
                            return {
                              ...p,
                              weekday_ids: already
                                ? current.filter((id) => id !== day.id)
                                : [...current, day.id],
                            };
                          });
                        }}
                      >
                        {day.name}
                      </Chip>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  অন্তত ২–৩ দিন সিলেক্ট করলে টিউটরদের জন্য বুঝতে সুবিধা হয়।
                </p>
              </div>

              {/* Salary slider (modern) */}
              <div>
                <FieldLabel>প্রস্তাবিত বেতন (রেঞ্জ)</FieldLabel>

                <div className="rounded-2xl border border-gray-200 bg-white/70 backdrop-blur p-4 md:p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        নির্বাচিত বেতন
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        স্লাইডার না বদলালে বেতন অপশনাল থাকবে
                      </p>
                    </div>

                    <div className="shrink-0">
                      <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700">
                        {form.min_salary && Number(form.min_salary) > 0
                          ? `${form.min_salary} টাকা/মাস`
                          : "নির্ধারিত নয়"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <input
                      type="range"
                      min="0"
                      max="50000"
                      step="500"
                      value={form.min_salary || 0}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setForm((p) => ({
                          ...p,
                          min_salary: value,
                          max_salary: value,
                        }));
                      }}
                      className="
                        w-full h-2 rounded-full appearance-none
                        bg-gradient-to-r from-indigo-500 via-indigo-400 to-emerald-400
                        outline-none
                        focus:ring-4 focus:ring-indigo-400/25
                        cursor-pointer
                        [&::-webkit-slider-thumb]:appearance-none
                        [&::-webkit-slider-thumb]:h-5
                        [&::-webkit-slider-thumb]:w-5
                        [&::-webkit-slider-thumb]:rounded-full
                        [&::-webkit-slider-thumb]:bg-white
                        [&::-webkit-slider-thumb]:border
                        [&::-webkit-slider-thumb]:border-gray-200
                        [&::-webkit-slider-thumb]:shadow
                        [&::-webkit-slider-thumb]:transition
                        [&::-webkit-slider-thumb]:active:scale-110
                        [&::-moz-range-thumb]:h-5
                        [&::-moz-range-thumb]:w-5
                        [&::-moz-range-thumb]:rounded-full
                        [&::-moz-range-thumb]:bg-white
                        [&::-moz-range-thumb]:border
                        [&::-moz-range-thumb]:border-gray-200
                        [&::-moz-range-thumb]:shadow
                      "
                    />

                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                        ০
                      </span>
                      <span className="inline-flex items-center gap-1">
                        ৫০,০০০+
                        <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gender */}
              <div>
                <FieldLabel>আপনি কোন জেন্ডারের টিউটর চাচ্ছেন?</FieldLabel>
                <div className="flex flex-wrap gap-3">
                  <Chip
                    active={form.gender === "Male"}
                    onClick={() => setForm((p) => ({ ...p, gender: "Male" }))}
                  >
                    পুরুষ
                  </Chip>
                  <Chip
                    active={form.gender === "Female"}
                    onClick={() => setForm((p) => ({ ...p, gender: "Female" }))}
                  >
                    মহিলা
                  </Chip>
                  <Chip
                    active={form.gender === "Any"}
                    onClick={() => setForm((p) => ({ ...p, gender: "Any" }))}
                  >
                    যেকোনো
                  </Chip>
                </div>
              </div>

              {/* Phone */}
              <div>
                <FieldLabel>ফোন</FieldLabel>
                <input
                  type="text"
                  className="text-black w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus:ring-4 focus:ring-indigo-400/25 focus:border-indigo-400 transition"
                  value={form.phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((p) => ({
                      ...p,
                      phone: value,
                      whatsapp: p.whatsapp_same_as_phone ? value : p.whatsapp,
                    }));
                  }}
                  required
                  placeholder="01XXXXXXXXX"
                />

                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="whatsapp_same_as_phone"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={form.whatsapp_same_as_phone}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((p) => ({
                        ...p,
                        whatsapp_same_as_phone: checked,
                        whatsapp: checked ? p.phone : "",
                      }));
                    }}
                  />
                  <label
                    htmlFor="whatsapp_same_as_phone"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    হোয়াটসঅ্যাপ নম্বর এবং ফোন নম্বর একই
                  </label>
                </div>
              </div>

              {/* WhatsApp (only if not same) */}
              {!form.whatsapp_same_as_phone && (
                <div>
                  <FieldLabel>হোয়াটসঅ্যাপ</FieldLabel>
                  <input
                    type="text"
                    className="text-black w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 focus:outline-none focus:ring-4 focus:ring-indigo-400/25 focus:border-indigo-400 transition"
                    value={form.whatsapp}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, whatsapp: e.target.value }))
                    }
                    required={!form.whatsapp_same_as_phone}
                    placeholder="01XXXXXXXXX"
                  />
                </div>
              )}

              {/* Submit + note */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold shadow-sm transition
                    hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-400/30
                    ${submitting ? "opacity-70 cursor-not-allowed" : ""}
                  `}
                >
                  {submitting ? "জমা হচ্ছে..." : "জমা দিন"}
                </button>

                <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                  নোট: লগইন ছাড়াই টিউশন পোস্ট করা যায়। পোস্টদাতা নিশ্চিত করছেন
                  যে তথ্যগুলো সত্য এবং তাঁর/তাদের শেয়ার করার অধিকার আছে।
                  ভুয়া/কপি করা/বিভ্রান্তিকর পোস্টের দায় পোস্টদাতার, এবং আমরা
                  প্রয়োজন হলে কোনো নোটিশ ছাড়াই পোস্ট রিভিউ/সংশোধন/অপসারণ করতে
                  পারি।
                </p>
              </div>
            </form>
          </div>

          {/* Subtle footer spacing */}
          <div className="h-10" />
        </div>
      </div>

      <Footer />
    </>
  );
}
